/** Merge tag catalog for Warm-up Sequences message text (insert-only; resolved at send time). */

export const WARM_UP_TAG_CATEGORIES = [
  { id: "bot_settings_general", label: "Bot settings — document" },
  { id: "bot_settings_knowledge_base", label: "Bot settings — knowledge base" },
  { id: "bot_settings_tone", label: "Bot settings — tone of voice" },
  { id: "bot_settings_scheduling", label: "Bot settings — scheduling" },
  { id: "bot_settings_qualification", label: "Bot settings — qualification logic" },
  { id: "chat_document", label: "Chat — document" },
  { id: "chat_client_data", label: "Chat — client data" },
  { id: "chat_scheduling", label: "Chat — scheduling" },
  { id: "chat_qualification_static", label: "Chat — qualification (default keys)" },
  { id: "chat_qualification_dynamic", label: "Chat — qualification (your bot questions)" },
  { id: "chat_validation", label: "Chat — validation & weather" },
  { id: "chat_orchestrator", label: "Chat — orchestrator" },
];

const WEATHER_ONLY = ["weather"];

export const DYNAMIC_VALUE_NOTE =
  "Per lead — this value is filled from each customer's chat when the message is sent.";

export function inferValueSource(tag) {
  const inner = tag.startsWith("{") && tag.endsWith("}") ? tag.slice(1, -1) : tag;
  if (inner.startsWith("bot_settings.")) return "bot_settings";
  return "chat";
}

function entry(tag, description, category, messageTypes, valueSource) {
  return {
    tag,
    description,
    category,
    messageTypes,
    valueSource: valueSource ?? inferValueSource(tag),
  };
}

export const WARM_UP_MERGE_TAGS = [
  // --- bot_settings — document ---
  entry("{bot_settings.bot_type}", "Bot type (e.g. roofing_v1)", "bot_settings_general"),
  entry("{bot_settings.email}", "Account email on bot settings", "bot_settings_general"),
  entry("{bot_settings.user_id}", "Tenant user ID", "bot_settings_general"),

  // --- knowledge_base — company ---
  entry(
    "{bot_settings.knowledge_base.company_name}",
    "Legal / primary company name",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.trading_name}",
    "Trading name shown to customers",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.agency_name}",
    "Agency name (legacy / real estate fallback)",
    "bot_settings_knowledge_base"
  ),
  entry("{bot_settings.knowledge_base.website_url}", "Company website URL", "bot_settings_knowledge_base"),
  entry("{bot_settings.knowledge_base.founded_year}", "Year company was founded", "bot_settings_knowledge_base"),
  entry(
    "{bot_settings.knowledge_base.last_rag_index_update}",
    "Last knowledge base index update timestamp",
    "bot_settings_knowledge_base"
  ),

  // office_location
  entry(
    "{bot_settings.knowledge_base.office_location.address}",
    "Office / depot street address",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.office_location.country}",
    "Office country",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.office_location.state}",
    "Office state or region",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.office_location.is_customer_facing}",
    "Whether customers can visit the office",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.office_location.geo.lat}",
    "Office latitude (after geocoding)",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.office_location.geo.lng}",
    "Office longitude (after geocoding)",
    "bot_settings_knowledge_base"
  ),

  // service_area
  entry(
    "{bot_settings.knowledge_base.service_area.description}",
    "Service area description",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.service_area.suburbs_or_lgas}",
    "Suburbs or LGAs served",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.service_area.base_postcode}",
    "Base postcode for distance calculations",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.service_area.max_drive_radius_km}",
    "Maximum drive radius in km",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.service_area.crosses_state_borders}",
    "Whether jobs cross state borders",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.service_area.rejection_message}",
    "Message when address is out of area",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.service_area.regional_callouts.allowed}",
    "Whether regional callouts are allowed",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.service_area.regional_callouts.min_job_value}",
    "Minimum job value for regional callouts",
    "bot_settings_knowledge_base"
  ),

  // credentials & legal
  entry(
    "{bot_settings.knowledge_base.credentials.registration_number}",
    "Business registration number (ABN/EIN)",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.credentials.license_holder_name}",
    "License holder name",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.credentials.license_number}",
    "License number",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.credentials.insurance}",
    "Insurance coverage summary",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.credentials.warranty_terms}",
    "Warranty terms",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.legal_and_compliance.privacy_policy_url}",
    "Privacy policy URL",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.legal_and_compliance.state_regulations}",
    "State regulations text (e.g. cooling-off)",
    "bot_settings_knowledge_base"
  ),

  // operations
  entry(
    "{bot_settings.knowledge_base.operations.office_hours.monday_to_friday.start}",
    "Office hours Mon–Fri start",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.operations.office_hours.monday_to_friday.end}",
    "Office hours Mon–Fri end",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.operations.office_hours.saturday.start}",
    "Office hours Saturday start",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.operations.office_hours.saturday.end}",
    "Office hours Saturday end",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.operations.office_hours.out_of_hours_message}",
    "Message shown outside office hours",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.operations.field_crew_hours.monday_to_friday.start}",
    "Field crew hours Mon–Fri start",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.operations.field_crew_hours.monday_to_friday.end}",
    "Field crew hours Mon–Fri end",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.operations.quoting_workflow.on_site_quoter_role}",
    "Role that performs on-site quotes",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.operations.quoting_workflow.description}",
    "Quoting workflow description",
    "bot_settings_knowledge_base"
  ),

  // handoff_messages
  entry(
    "{bot_settings.knowledge_base.handoff_settings.handoff_messages.general_escalation}",
    "General human handoff message",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.handoff_settings.handoff_messages.emergency_escalation}",
    "Emergency handoff message",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.handoff_settings.roles[0].name}",
    "First handoff role contact name (use index for others)",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.handoff_settings.roles[0].phone}",
    "First handoff role phone",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.handoff_settings.roles[0].email}",
    "First handoff role email",
    "bot_settings_knowledge_base"
  ),

  // pricing_policy
  entry(
    "{bot_settings.knowledge_base.pricing_policy.quote_rules.callout_fee.amount}",
    "Repair callout fee amount",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.pricing_policy.quote_rules.callout_fee_waived_if_proceeds}",
    "Callout fee waived if customer proceeds",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.pricing_policy.job_requirements.minimum_job_value}",
    "Minimum job value",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.pricing_policy.job_requirements.deposit_required}",
    "Deposit required on acceptance",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.pricing_policy.payment_and_finance.tax_status}",
    "Tax status (e.g. GST inclusive)",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.pricing_policy.payment_and_finance.financing_options.provider_name}",
    "Financing provider name",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.pricing_policy.ballpark_pricing.enforce_no_exact_prices}",
    "Whether exact prices are blocked in chat",
    "bot_settings_knowledge_base"
  ),

  // competitive_strategy
  entry(
    "{bot_settings.knowledge_base.competitive_strategy.core_differentiator}",
    "Core differentiator / USP",
    "bot_settings_knowledge_base"
  ),

  // social_networks
  entry(
    "{bot_settings.knowledge_base.social_networks[0].network}",
    "First social network type",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.social_networks[0].url}",
    "First social network URL",
    "bot_settings_knowledge_base"
  ),

  // services_offered (roofing defaults)
  entry(
    "{bot_settings.knowledge_base.services_offered.metal_roof_replacement}",
    "Metal roof replacement offered status",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.services_offered.tile_roof_replacement}",
    "Tile roof replacement offered status",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.services_offered.roof_repairs_leak_detection}",
    "Roof repairs / leak detection offered status",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.services_offered.storm_hail_damage_repair}",
    "Storm / hail damage repair offered status",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.services_offered.insurance_claim_work}",
    "Insurance claim work offered status",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.services_offered.gutters_fascia_downpipes}",
    "Gutters / fascia / downpipes offered status",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.services_offered.commercial_industrial_roofing}",
    "Commercial / industrial roofing offered status",
    "bot_settings_knowledge_base"
  ),
  entry(
    "{bot_settings.knowledge_base.services_offered.annual_maintenance}",
    "Annual maintenance offered status",
    "bot_settings_knowledge_base"
  ),

  // --- tone_of_voice ---
  entry("{bot_settings.tone_of_voice.bot_name}", "Bot persona name", "bot_settings_tone"),
  entry("{bot_settings.tone_of_voice.style}", "Tone style identifier", "bot_settings_tone"),
  entry("{bot_settings.tone_of_voice.tagline}", "Company tagline in bot voice", "bot_settings_tone"),
  entry("{bot_settings.tone_of_voice.use_emojis}", "Whether emojis are allowed", "bot_settings_tone"),
  entry("{bot_settings.tone_of_voice.use_aussie_slang}", "Whether Aussie slang is used", "bot_settings_tone"),
  entry("{bot_settings.tone_of_voice.disclose_ai_status}", "Whether bot discloses it is AI", "bot_settings_tone"),

  // --- scheduling ---
  entry("{bot_settings.scheduling.meeting_type}", "Default meeting type label", "bot_settings_scheduling"),
  entry(
    "{bot_settings.scheduling.default_duration_minutes}",
    "Default appointment duration (minutes)",
    "bot_settings_scheduling"
  ),
  entry("{bot_settings.scheduling.inspection_hours}", "Inspection hours (legacy string)", "bot_settings_scheduling"),
  entry("{bot_settings.scheduling.business_hours}", "Business hours (legacy string)", "bot_settings_scheduling"),
  entry(
    "{bot_settings.scheduling.daylight_rule_enforced}",
    "Daylight scheduling rule enforced",
    "bot_settings_scheduling"
  ),
  entry(
    "{bot_settings.scheduling.bot_schedule.timezone}",
    "Bot schedule timezone",
    "bot_settings_scheduling"
  ),
  entry(
    "{bot_settings.scheduling.bot_schedule.enabled}",
    "Whether bot schedule gate is enabled",
    "bot_settings_scheduling"
  ),
  entry(
    "{bot_settings.scheduling.operating_hours.monday_to_friday.start}",
    "Booking window Mon–Fri start",
    "bot_settings_scheduling"
  ),
  entry(
    "{bot_settings.scheduling.operating_hours.monday_to_friday.end}",
    "Booking window Mon–Fri end",
    "bot_settings_scheduling"
  ),
  entry(
    "{bot_settings.scheduling.operating_hours.public_holidays.surcharge_fee}",
    "Public holiday surcharge fee",
    "bot_settings_scheduling"
  ),
  entry(
    "{bot_settings.scheduling.logistics.travel_buffer_minutes}",
    "Travel buffer between appointments (minutes)",
    "bot_settings_scheduling"
  ),
  entry(
    "{bot_settings.scheduling.logistics.window_size_hours}",
    "Arrival window size (hours)",
    "bot_settings_scheduling"
  ),
  entry(
    "{bot_settings.scheduling.booking_rules.min_lead_time_hours}",
    "Minimum lead time before booking (hours)",
    "bot_settings_scheduling"
  ),
  entry(
    "{bot_settings.scheduling.booking_rules.max_lead_time_days}",
    "Maximum days ahead for booking",
    "bot_settings_scheduling"
  ),
  entry(
    "{bot_settings.scheduling.team_calendars[0].staff_name}",
    "First team calendar staff name",
    "bot_settings_scheduling"
  ),
  entry(
    "{bot_settings.scheduling.team_calendars[0].calendar_id}",
    "First team calendar ID",
    "bot_settings_scheduling"
  ),

  // appointment_types
  entry(
    "{bot_settings.scheduling.appointment_types.free_quote_inspection.duration_minutes}",
    "Free quote inspection duration (minutes)",
    "bot_settings_scheduling"
  ),
  entry(
    "{bot_settings.scheduling.appointment_types.free_quote_inspection.price}",
    "Free quote inspection price",
    "bot_settings_scheduling"
  ),
  entry(
    "{bot_settings.scheduling.appointment_types.emergency_leak_callout.price}",
    "Emergency leak callout price",
    "bot_settings_scheduling"
  ),
  entry(
    "{bot_settings.scheduling.appointment_types.repair_booking.duration_minutes}",
    "Repair booking duration (minutes)",
    "bot_settings_scheduling"
  ),
  entry(
    "{bot_settings.scheduling.appointment_types.warranty_callback.attendee}",
    "Warranty callback attendee role",
    "bot_settings_scheduling"
  ),
  entry(
    "{bot_settings.scheduling.appointment_types.annual_maintenance_booking.price}",
    "Annual maintenance booking price",
    "bot_settings_scheduling"
  ),
  entry(
    "{bot_settings.scheduling.appointment_types.restoration_consult.duration_minutes}",
    "Restoration consult duration (minutes)",
    "bot_settings_scheduling"
  ),
  entry(
    "{bot_settings.scheduling.appointment_types.commercial_site_walk.attendee}",
    "Commercial site walk attendee",
    "bot_settings_scheduling"
  ),

  // --- qualification_logic ---
  entry(
    "{bot_settings.qualification_logic.unqualified_action}",
    "Action when lead is unqualified",
    "bot_settings_qualification"
  ),
  entry(
    "{bot_settings.qualification_logic.strict_qualification_gate}",
    "Strict qualification gate enabled",
    "bot_settings_qualification"
  ),
  entry(
    "{bot_settings.qualification_logic.skip_qualification_if_known_user}",
    "Skip qualification for returning customers",
    "bot_settings_qualification"
  ),
  entry(
    "{bot_settings.qualification_logic.disqualification_rules.rejection_messages.out_of_area}",
    "Rejection message: out of service area",
    "bot_settings_qualification"
  ),
  entry(
    "{bot_settings.qualification_logic.disqualification_rules.rejection_messages.service_not_offered}",
    "Rejection message: service not offered",
    "bot_settings_qualification"
  ),
  entry(
    "{bot_settings.qualification_logic.disqualification_rules.rejection_messages.property_type}",
    "Rejection message: property type not supported",
    "bot_settings_qualification"
  ),
  entry(
    "{bot_settings.qualification_logic.disqualification_rules.rejection_messages.tenant_no_authority}",
    "Rejection message: tenant without authority",
    "bot_settings_qualification"
  ),
  entry(
    "{bot_settings.qualification_logic.disqualification_rules.rejection_messages.asbestos_referral}",
    "Rejection message: asbestos referral",
    "bot_settings_qualification"
  ),
  entry(
    "{bot_settings.qualification_logic.disqualification_rules.rejection_messages.height_limit}",
    "Rejection message: building height limit",
    "bot_settings_qualification"
  ),
  entry(
    "{bot_settings.qualification_logic.disqualification_rules.rejection_messages.default_polite_no}",
    "Default polite rejection message",
    "bot_settings_qualification"
  ),

  // --- chat document ---
  entry("{chat.id}", "Chat UUID", "chat_document"),
  entry("{chat.lead_name}", "Lead display name from channel", "chat_document"),
  entry("{chat.lead_contact}", "Lead contact ID (phone, GHL id, etc.)", "chat_document"),
  entry("{chat.platform}", "Messaging platform (whatsapp, telegram, ghl, …)", "chat_document"),
  entry("{chat.ai_active}", "Whether AI is active in this chat", "chat_document"),
  entry("{chat.ghl_opportunity_id}", "GoHighLevel opportunity ID if synced", "chat_document"),
  entry("{chat.timestamps.created_at}", "Chat created at", "chat_document"),
  entry("{chat.timestamps.updated_at}", "Chat last updated at", "chat_document"),
  entry("{chat.metrics.pipeline_stage}", "Funnel stage (in_progress, meeting_booked, …)", "chat_document"),
  entry("{chat.metrics.lead_crm}", "CRM answer from qualification or TBD", "chat_document"),
  entry("{chat.metrics.ai_response_count}", "Number of AI responses in chat", "chat_document"),

  // --- client_data ---
  entry("{chat.client_data.client_name}", "Client name collected in conversation", "chat_client_data"),
  entry("{chat.client_data.property_address}", "Property street address", "chat_client_data"),
  entry("{chat.client_data.property_state}", "Property state / region", "chat_client_data"),
  entry("{chat.client_data.property_country}", "Property country", "chat_client_data"),
  entry("{chat.client_data.property_zip_code}", "Property postcode / ZIP", "chat_client_data"),
  entry("{chat.client_data.property_geo.lat}", "Property latitude", "chat_client_data"),
  entry("{chat.client_data.property_geo.lng}", "Property longitude", "chat_client_data"),
  entry("{chat.client_data.distance_to_base_km}", "Distance from property to base (km)", "chat_client_data"),
  entry("{chat.client_data.contact_phone}", "Contact phone number", "chat_client_data"),
  entry("{chat.client_data.contact_email}", "Contact email", "chat_client_data"),
  entry("{chat.client_data.issue_summary}", "Short issue summary for crew / CRM", "chat_client_data"),
  entry("{chat.client_data.quote_reference_number}", "Quote reference number", "chat_client_data"),
  entry("{chat.client_data.square_footage}", "Property square footage if collected", "chat_client_data"),
  entry("{chat.client_data.original_job_reference}", "Original job reference (warranty / return)", "chat_client_data"),

  // --- scheduling_context ---
  entry(
    "{chat.scheduling.selected_appointment_type}",
    "Selected appointment type key",
    "chat_scheduling"
  ),
  entry("{chat.scheduling.confirmed_slot}", "Confirmed calendar slot (ISO / string)", "chat_scheduling"),
  entry(
    "{chat.scheduling.requested_timeframe}",
    "Customer preferred inspection timeframe",
    "chat_scheduling"
  ),
  entry("{chat.scheduling.calculated_price}", "Calculated price for booking", "chat_scheduling"),
  entry("{chat.scheduling.booking_reference_id}", "Calendar event / booking reference ID", "chat_scheduling"),
  entry("{chat.scheduling.booking_status}", "Booking status", "chat_scheduling"),
  entry("{chat.scheduling.callback_preferred}", "Customer requested a phone callback", "chat_scheduling"),
  entry("{chat.scheduling.callback_time}", "Callback time or description", "chat_scheduling"),
  entry(
    "{chat.scheduling.days_until_meeting}",
    "Days until confirmed meeting (resolved at send time)",
    "chat_scheduling"
  ),
  entry(
    "{chat.scheduling.meeting_date}",
    "Formatted meeting date (resolved at send time)",
    "chat_scheduling"
  ),
  entry(
    "{chat.scheduling.meeting_time}",
    "Formatted meeting time (resolved at send time)",
    "chat_scheduling"
  ),

  // --- qualification static keys ---
  entry("{chat.qualification.property_address}", "Qualification answer: property address", "chat_qualification_static"),
  entry("{chat.qualification.ownership_status}", "Qualification answer: owner / tenant / agent", "chat_qualification_static"),
  entry("{chat.qualification.property_type}", "Qualification answer: property type", "chat_qualification_static"),
  entry("{chat.qualification.storey_count}", "Qualification answer: storey count", "chat_qualification_static"),
  entry("{chat.qualification.roof_type}", "Qualification answer: roof material", "chat_qualification_static"),
  entry("{chat.qualification.roof_age}", "Qualification answer: roof age", "chat_qualification_static"),
  entry("{chat.qualification.issue_details}", "Qualification answer: issue details", "chat_qualification_static"),
  entry("{chat.qualification.timeline_urgency}", "Qualification answer: urgency / timeline", "chat_qualification_static"),
  entry("{chat.qualification.previous_quotes}", "Qualification answer: previous quotes", "chat_qualification_static"),
  entry("{chat.qualification.insurance_status}", "Qualification answer: insurance claim", "chat_qualification_static"),
  entry("{chat.qualification.has_photos}", "Qualification answer: photos available", "chat_qualification_static"),
  entry(
    "{chat.qualification.preferred_contact_method}",
    "Qualification answer: preferred contact method",
    "chat_qualification_static"
  ),
  entry(
    "{chat.qualification.preferred_callback_time}",
    "Qualification answer: preferred visit window (legacy key name)",
    "chat_qualification_static"
  ),
  entry("{chat.qualification.lead_source}", "Qualification answer: how they heard about you", "chat_qualification_static"),
  entry("{chat.qualification.budget_range}", "Qualification answer: budget range", "chat_qualification_static"),
  entry("{chat.qualification.current_crm}", "Qualification answer: CRM system (metrics source)", "chat_qualification_static"),

  // --- validation & weather ---
  entry("{chat.validation.qualification_complete}", "Whether qualification is complete", "chat_validation"),
  entry("{chat.validation.is_disqualified}", "Whether lead was disqualified", "chat_validation"),
  entry("{chat.validation.disqualification_reason}", "Disqualification reason", "chat_validation"),
  entry("{chat.validation.location_approved}", "Whether property is in service area", "chat_validation"),
  entry("{chat.validation.weather_approved}", "Whether weather allows work", "chat_validation"),
  entry(
    "{chat.validation.environmental_context.forecasts[0].will_rain}",
    "Weather forecast: rain expected",
    "chat_validation",
    WEATHER_ONLY
  ),
  entry(
    "{chat.validation.environmental_context.forecasts[0].rain_mm}",
    "Weather forecast: rainfall (mm)",
    "chat_validation",
    WEATHER_ONLY
  ),
  entry(
    "{chat.validation.environmental_context.forecasts[0].time_checked}",
    "Weather forecast: last checked time",
    "chat_validation",
    WEATHER_ONLY
  ),
  entry(
    "{chat.validation.environmental_context.sunrise}",
    "Sunrise time at property",
    "chat_validation",
    WEATHER_ONLY
  ),
  entry(
    "{chat.validation.environmental_context.sunset}",
    "Sunset time at property",
    "chat_validation",
    WEATHER_ONLY
  ),

  // --- orchestrator ---
  entry("{chat.orchestrator.handoff_required}", "Whether handoff to human is required", "chat_orchestrator"),
  entry("{chat.orchestrator.handoff_reason}", "Handoff reason", "chat_orchestrator"),
  entry("{chat.orchestrator.is_known_user}", "Returning / known customer flag", "chat_orchestrator"),
  entry("{chat.orchestrator.human_request_count}", "Times customer asked for a human", "chat_orchestrator"),
];

/** Warm-up picker: entire categories hidden from the Insert merge tag modal. */
export const HIDDEN_WARM_UP_CATEGORY_IDS = new Set([
  "bot_settings_scheduling",
  "bot_settings_qualification",
  "chat_validation",
  "chat_orchestrator",
]);

/** Warm-up picker: individual tags hidden from the modal (full catalog unchanged). */
const HIDDEN_WARM_UP_TAG_LIST = [
  "{bot_settings.bot_type}",
  "{bot_settings.user_id}",
  "{bot_settings.knowledge_base.agency_name}",
  "{bot_settings.knowledge_base.last_rag_index_update}",
  "{bot_settings.knowledge_base.office_location.is_customer_facing}",
  "{bot_settings.knowledge_base.service_area.description}",
  "{bot_settings.knowledge_base.service_area.suburbs_or_lgas}",
  "{bot_settings.knowledge_base.service_area.base_postcode}",
  "{bot_settings.knowledge_base.service_area.crosses_state_borders}",
  "{bot_settings.knowledge_base.service_area.rejection_message}",
  "{bot_settings.knowledge_base.service_area.regional_callouts.allowed}",
  "{bot_settings.knowledge_base.service_area.regional_callouts.min_job_value}",
  "{bot_settings.knowledge_base.credentials.registration_number}",
  "{bot_settings.knowledge_base.credentials.license_holder_name}",
  "{bot_settings.knowledge_base.operations.office_hours.out_of_hours_message}",
  "{bot_settings.knowledge_base.operations.quoting_workflow.on_site_quoter_role}",
  "{bot_settings.knowledge_base.operations.quoting_workflow.description}",
  "{bot_settings.knowledge_base.handoff_settings.handoff_messages.general_escalation}",
  "{bot_settings.knowledge_base.handoff_settings.handoff_messages.emergency_escalation}",
  "{bot_settings.knowledge_base.handoff_settings.roles[0].name}",
  "{bot_settings.knowledge_base.handoff_settings.roles[0].phone}",
  "{bot_settings.knowledge_base.handoff_settings.roles[0].email}",
  "{bot_settings.knowledge_base.pricing_policy.quote_rules.callout_fee_waived_if_proceeds}",
  "{bot_settings.knowledge_base.pricing_policy.ballpark_pricing.enforce_no_exact_prices}",
  "{bot_settings.knowledge_base.services_offered.metal_roof_replacement}",
  "{bot_settings.knowledge_base.services_offered.tile_roof_replacement}",
  "{bot_settings.knowledge_base.services_offered.roof_repairs_leak_detection}",
  "{bot_settings.knowledge_base.services_offered.storm_hail_damage_repair}",
  "{bot_settings.knowledge_base.services_offered.insurance_claim_work}",
  "{bot_settings.knowledge_base.services_offered.gutters_fascia_downpipes}",
  "{bot_settings.knowledge_base.services_offered.commercial_industrial_roofing}",
  "{bot_settings.knowledge_base.services_offered.annual_maintenance}",
  "{bot_settings.tone_of_voice.style}",
  "{bot_settings.tone_of_voice.tagline}",
  "{bot_settings.tone_of_voice.use_emojis}",
  "{bot_settings.tone_of_voice.use_aussie_slang}",
  "{bot_settings.tone_of_voice.disclose_ai_status}",
  "{chat.id}",
  "{chat.ai_active}",
  "{chat.ghl_opportunity_id}",
  "{chat.timestamps.created_at}",
  "{chat.timestamps.updated_at}",
  "{chat.metrics.pipeline_stage}",
  "{chat.metrics.lead_crm}",
  "{chat.metrics.ai_response_count}",
  "{chat.client_data.quote_reference_number}",
  "{chat.client_data.square_footage}",
  "{chat.client_data.original_job_reference}",
  "{chat.scheduling.selected_appointment_type}",
  "{chat.scheduling.requested_timeframe}",
  "{chat.scheduling.calculated_price}",
  "{chat.scheduling.booking_reference_id}",
  "{chat.scheduling.callback_preferred}",
  "{chat.scheduling.callback_time}",
  "{chat.scheduling.days_until_meeting}",
  "{chat.scheduling.meeting_date}",
  "{chat.scheduling.meeting_time}",
  "{chat.qualification.preferred_callback_time}",
  "{chat.qualification.lead_source}",
  "{chat.qualification.budget_range}",
  "{chat.qualification.current_crm}",
];

export const HIDDEN_WARM_UP_TAGS = new Set(HIDDEN_WARM_UP_TAG_LIST);

export function isWarmUpTagVisible(tagEntry) {
  if (!tagEntry) return false;
  if (HIDDEN_WARM_UP_CATEGORY_IDS.has(tagEntry.category)) return false;
  if (HIDDEN_WARM_UP_TAGS.has(tagEntry.tag)) return false;
  return true;
}

/** Filter tags shown in the Warm-up Sequences tag picker (UI only). */
export function filterTagsForWarmUpPicker(tags) {
  return tags.filter(isWarmUpTagVisible);
}

const STATIC_TAG_SET = new Set(WARM_UP_MERGE_TAGS.map((t) => t.tag));

/** Tags visible for a given warm-up message type. */
export function filterTagsByMessageType(tags, messageType) {
  return tags.filter((t) => {
    if (!t.messageTypes || t.messageTypes.length === 0) return true;
    return t.messageTypes.includes(messageType);
  });
}

/** Group tags by category id; preserves WARM_UP_TAG_CATEGORIES order. */
export function groupTagsByCategory(tags) {
  const byCategory = new Map();
  for (const t of tags) {
    if (!byCategory.has(t.category)) byCategory.set(t.category, []);
    byCategory.get(t.category).push(t);
  }
  const order = WARM_UP_TAG_CATEGORIES.map((c) => c.id);
  const dynamicId = "chat_qualification_dynamic";
  if (!order.includes(dynamicId)) order.push(dynamicId);

  return order
    .filter((id) => byCategory.has(id))
    .map((id) => ({
      categoryId: id,
      label:
        WARM_UP_TAG_CATEGORIES.find((c) => c.id === id)?.label ??
        (id === dynamicId ? "Chat — qualification (your bot questions)" : id),
      tags: byCategory.get(id),
    }));
}

/**
 * Build dynamic qualification tags from bot settings questions.
 * Skips keys already in the static catalog.
 */
export function buildDynamicQualificationTags(questions) {
  if (!Array.isArray(questions)) return [];
  const out = [];
  for (const q of questions) {
    const key = typeof q?.key === "string" ? q.key.trim() : "";
    if (!key) continue;
    const tag = `{chat.qualification.${key}}`;
    if (STATIC_TAG_SET.has(tag)) continue;
    const questionText = typeof q?.question === "string" ? q.question.trim() : "";
    out.push({
      tag,
      description: questionText ? `Answer to: ${questionText}` : `Answer to qualification key "${key}"`,
      category: "chat_qualification_dynamic",
      valueSource: "chat",
    });
  }
  return out;
}

/** Merge static tags with dynamic qual tags (dedupe by tag string). */
export function mergeWarmUpTags(staticTags, dynamicTags) {
  const seen = new Set(staticTags.map((t) => t.tag));
  const merged = [...staticTags];
  for (const t of dynamicTags) {
    if (seen.has(t.tag)) continue;
    seen.add(t.tag);
    merged.push(t);
  }
  return merged;
}

/** Sanitize tag for data-testid (alphanumeric + hyphens). */
export function tagToTestId(tag) {
  return tag.replace(/[{}]/g, "").replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-|-$/g, "");
}
